import { axiosInstance } from "@/lib/axios-instance";

interface CreateCompanyPitchboxProps {
  userId: string;
  workspaceId: string;
  companyName: string;
  companyWebsite: string;
}

interface changeStatusResponse {
  status: string;
  message?: string;
}

export interface RecentDeal {
  id: string;
  name: string;
  website?: string;
  status: string;
  /** ISO date the deal was added. */
  added_on: string;
  /** Avatar file(s) for CompanyAvatar — only file_type/file_url are used. */
  company_files: { file_type: string; file_url?: string }[];
}

export interface DashboardData {
  status: string;
  data: {
    total_deals: number;
    reports_generated: number;
    total_reviews: number;
    portfolio_companies: number;
    leads_per_month: number[];
    pipeline_stages?: {
      new?: number;
      review?: number;
      deepdiligence?: number;
      firstdiligence?: number;
      offer?: number;
    };
    recent_metrics: {
      total_deals: number;
      reports_generated: number;
      total_reviews: number;
      portfolio_companies: number;
    };
    recent_deals?: RecentDeal[];
  };
}

export interface addDealNoteResponse {
  status: string;
  application: {
    reports_in_progress: {
      deal_memo: boolean;
    };
    _id: string;
    applicant_company: string;
    pertains_to_opportunity: string;
    action_type: string;
    application_status: string;
    created_on: string;
    deal_info: Array<{
      field_name: string;
      field_value: string | number | string[];
      field_state: string;
    }>;
    deal_notes: Array<{
      _id: string;
      note: string;
      created_by_user: string;
      created_by_user_id: string;
      created_on: string;
      tags?: string[];
    }>;
    __v: number;
    fundraising?: {
      total_amount: string;
      committed_amount: string;
      lead_investor: string;
    };
    website_summary?: string;
    changed_by: string;
  };
}

export const getDashboardData = async (
  workspaceId: string
): Promise<DashboardData> => {
  const response = await axiosInstance.get("/fetch_dashboard_for_opportunity", {
    params: {
      opportunity_id: workspaceId,
    },
  });
  return response.data;
};

interface FlaggedCompanyResponse {
  status: string;
  result: {
    _id: string;
    flagged_company: string;
    opportunity: string;
    flagged_by: string;
    created_on: string;
    __v: number;
  };
}

interface DeleteFlaggedCompanyResponse {
  status: string;
}

export const dealsApi = {
  getDealById: async (workspaceId: string, companyId: string) => {
    const response = await axiosInstance.get("/fetch_single_deal", {
      params: {
        opportunity_id: workspaceId,
        company_id: companyId,
      },
    });
    if (response.data.status !== "success") {
      throw new Error(response.data.message || "Failed to fetch deal");
    }
    return response.data.application;
  },
  getSimpleDealList: async (workspaceId: string) => {
    const response = await axiosInstance.get("/fetch_simple_deal_list", {
      params: {
        opportunity_id: workspaceId,
      },
    });
    if (response.data.status !== "success") {
      throw new Error("Failed to fetch simple deal list");
    }

    const deals = response.data.data || [];

    // Debug logging for v2 data sufficiency
    deals.forEach((deal: any, index: number) => {
      if (deal.aiGenerations?.datasufficiencyscores_v2?.length > 0) {
        console.log(
          `Deal ${index} (${deal.company_name}) has v2 data sufficiency:`,
          deal.aiGenerations.datasufficiencyscores_v2
        );
      }
    });

    // Log raw gap_analysis question keys BEFORE Zod strips unknown fields
    const firstWithGap = deals.find((d: any) => d.aiGenerations?.gap_analysis?.length > 0);
    if (firstWithGap) {
      const rawQ = firstWithGap.aiGenerations.gap_analysis[0]?.questions?.[0];
      console.log('[RAW gap_analysis question keys]', rawQ ? Object.keys(rawQ) : 'no questions');
      console.log('[RAW gap_analysis question sample]', rawQ);
    }

    return deals;
  },
  changeStatus: async (
    dealId: string,
    status: string,
    changedBy: string,
    workspaceId: string
  ): Promise<changeStatusResponse> => {
    const response = await axiosInstance.post("/edit_status", {
      applicant_company: dealId,
      application_status: status,
      changed_by: changedBy,
      pertains_to_opportunity: workspaceId,
    });
    return response.data;
  },
  bulkChangeStatus: async (
    workspaceId: string,
    dealIds: string[],
    status: string,
    changedBy: string
  ): Promise<changeStatusResponse> => {
    const response = await axiosInstance.post("/bulk_edit_status", {
      deal_ids: dealIds,
      new_status: status,
      changed_by: changedBy,
      workspace_id: workspaceId,
    });
    return response.data;
  },
  addDealNote: async (
    companyId: string,
    note: string,
    workspaceId: string,
    createdBy: string,
    tags?: string[]
  ): Promise<addDealNoteResponse> => {
    const response = await axiosInstance.post("/add_deal_note", {
      company_id: companyId,
      note: note,
      opportunity_id: workspaceId,
      created_by: createdBy,
      tags: tags || [],
    });
    return response.data;
  },
  editDealNote: async (
    noteId: string,
    note: string,
    workspaceId: string,
    companyId: string,
    createdBy: string,
    tags?: string[]
  ): Promise<addDealNoteResponse> => {
    const response = await axiosInstance.post("/edit_deal_note", {
      note_id: noteId,
      note: note,
      opportunity_id: workspaceId,
      company_id: companyId,
      created_by: createdBy,
      tags: tags || [],
    });
    return response.data;
  },
  deleteDealNote: async (
    noteId: string,
    workspaceId: string,
    companyId: string
  ): Promise<addDealNoteResponse> => {
    const response = await axiosInstance.post("/delete_deal_note", {
      note_id: noteId,
      opportunity_id: workspaceId,
      company_id: companyId,
    });
    return response.data;
  },
  fetchFlaggedCompanies: async (workspaceId: string) => {
    const response = await axiosInstance.get("/fetch_simple_deal_list", {
      params: {
        opportunity_id: workspaceId,
        archived: true,
      },
    });
    if (response.data.status !== "success") {
      throw new Error("Failed to fetch simple deal list");
    }
    return response.data.data || [];
  },
  removeFlaggedCompany: async (
    workspaceId: string,
    companyId: string
  ): Promise<DeleteFlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      `/remove_flag_from_company_in_opportunity`,
      {
        opportunity_id: workspaceId,
        company_id: companyId,
      }
    );
    return response.data;
  },
  bulkRemoveFlaggedCompany: async (
    workspaceId: string,
    companyIds: string[]
  ): Promise<DeleteFlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      `/bulk_remove_flag_from_companies_in_opportunity`,
      {
        opportunity_id: workspaceId,
        company_ids: companyIds,
      }
    );
    return response.data;
  },
};
export const createDeal = {
  createCompanyPitchbox: async (
    CreateCompanyPitchboxProps: CreateCompanyPitchboxProps
  ) => {
    const response = await axiosInstance.post("/create_company_pitchbox", {
      user_id: CreateCompanyPitchboxProps.userId,
      opportunity_id: CreateCompanyPitchboxProps.workspaceId,
      company_name: CreateCompanyPitchboxProps.companyName,
      company_website: CreateCompanyPitchboxProps.companyWebsite,
    });

    return response.data;
  },
  createOpportunityApplication: async (
    companyId: string,
    workspaceId: string,
    selectedStatus?: string,
    source?: string
  ) => {
    const response = await axiosInstance.post(
      "/create_opportunity_application",
      {
        applicant_company: companyId,
        pertains_to_opportunity: workspaceId,
        source: source || "",
        application_status: selectedStatus || "New",
      }
    );
    return response.data.result;
  },

  createFlaggedCompany: async (
    workspaceId: string,
    companyId: string,
    userId: string
  ): Promise<FlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      "/create_flagged_company_in_opportunity",
      {
        flagged_company: companyId,
        opportunity: workspaceId,
        flagged_by: userId,
      }
    );
    return response.data;
  },
  bulkCreateFlaggedCompany: async (
    workspaceId: string,
    companyIds: string[],
    userId: string
  ): Promise<FlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      "/bulk_create_flagged_companies_in_opportunity",
      {
        company_ids: companyIds,
        opportunity_id: workspaceId,
        flagged_by: userId,
      }
    );
    return response.data;
  },
  deleteFlaggedCompany: async (
    workspaceId: string,
    companyId: string
  ): Promise<DeleteFlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      "/delete_flagged_company_from_opportunity",
      {
        opportunity_id: workspaceId,
        company_id: companyId,
        applicant_rejection_email: {
          header: "Thank you for applying!",
          body: "We are sorry to inform you that we are not able to move forward with your application at this time.\n",
          email: "",
        },
        isStealth: true,
      }
    );
    return response.data;
  },
  bulkDeleteFlaggedCompany: async (
    workspaceId: string,
    companyIds: string[]
  ): Promise<DeleteFlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      "/bulk_delete_companies_from_opportunity",
      {
        opportunity_id: workspaceId,
        company_ids: companyIds,
        applicant_rejection_email: {
          header: "Thank you for applying!",
          body: "We are sorry to inform you that we are not able to move forward with your application at this time.\n",
          email: "",
        },
        isStealth: true,
      }
    );
    return response.data;
  },
};

interface EditDealInfoRequest {
  workspaceId: string;
  companyId: string;
  fieldName: string;
  fieldValue: string | number | boolean | string[] | Record<string, unknown>;
}

interface EditDealInfoResponse {
  status: string;
  message?: string;
  updatedApplication?: {
    applicant_company: string;
    pertains_to_opportunity: string;
    changed_by?: string;
    custom_text_a1?: string;
    custom_text_a2?: string;
    application_status?: string;
    action_type?: "pending" | "approved" | "declined" | "reviewed";
    created_on: Date;
    deal_info?: Array<{
      field_name: string;
      field_value: string | number | boolean;
      field_state: "approved" | "declined" | "pending";
    }>;
    fundraising?: {
      total_amount?: string;
      committed_amount?: string;
      lead_investor?: string;
    };
    website_summary?: string;
    deal_notes?: Array<{
      note: string;
      created_on: Date;
      created_by_user_id: string;
      created_by_user: string;
    }>;
    reports_in_progress?: {
      deal_memo: {
        type: boolean;
        updated_at: Date;
      };
    };
  };
  error?: string;
}

export const editDealInfo = {
  editDealInfo: async (
    EditDealInfoRequest: EditDealInfoRequest
  ): Promise<EditDealInfoResponse> => {
    const response = await axiosInstance.post("/edit_deal_info", {
      opportunity_id: EditDealInfoRequest.workspaceId,
      company_id: EditDealInfoRequest.companyId,
      field_name: EditDealInfoRequest.fieldName,
      field_value: EditDealInfoRequest.fieldValue,
      field_state: "approved",
    });
    return response.data;
  },
};

interface EditCompanyInfoRequest {
  companyId: string;
  fieldName: string;
  fieldValue: string | number | boolean | string[];
}

interface EditCompanyInfoResponse {
  status: string;
  message?: string;
  result?: {
    company_id: string;
    company_name: string;
    company_website: string;
    address: string;
    short_bio: string;
    business_model: string;
    industry: string[];
    stage: number;
  };
}

export const editCompanyInfo = {
  editCompanyInfo: async (
    EditCompanyInfoRequest: EditCompanyInfoRequest
  ): Promise<EditCompanyInfoResponse> => {
    const response = await axiosInstance.post(
      `/edit_company_during_application?company_id=${EditCompanyInfoRequest.companyId}`,
      {
        [EditCompanyInfoRequest.fieldName]: EditCompanyInfoRequest.fieldValue,
      }
    );
    return response.data;
  },
};

export const editSource = {
  editSource: async (
    companyId: string,
    workspaceId: string,
    source: string
  ) => {
    const response = await axiosInstance.post("/edit_source", {
      company_id: companyId,
      opportunity_id: workspaceId,
      source: source,
    });
    return response.data;
  },
};

interface WorkflowStatus {
  colour: string;
  title: string;
  description: string;
  order: number;
}

export interface StatusMigrationResult {
  from: string;
  to: string;
  modified_count: number;
}

export const editOpportunityStatuses = {
  editOpportunityStatuses: async (
    workspaceId: string,
    statuses: WorkflowStatus[],
    migrations?: Record<string, string>
  ): Promise<{
    result: unknown;
    migration_results: StatusMigrationResult[];
  }> => {
    const response = await axiosInstance.post("/edit_opportunity_statuses", {
      opportunity_id: workspaceId,
      statuses: statuses,
      ...(migrations ? { migrations } : {}),
    });

    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }

    return {
      result: response.data.result,
      migration_results: response.data.migration_results ?? [],
    };
  },
};

export const getStatusCounts = async (
  workspaceId: string
): Promise<Record<string, number>> => {
  const response = await axiosInstance.get("/get_status_counts", {
    params: { workspace_id: workspaceId },
  });

  if (response.data.status === "error") {
    throw new Error(response.data.message);
  }

  return response.data.counts ?? {};
};

interface ActivateDealResponse {
  status: string;
  message?: string;
  data: {
    tracked: number;
    maxLimit: number;
    newStatus?: string;
  };
}

export const activateDeal = async (
  workspaceId: string,
  dealId: string,
  nextStage?: string
): Promise<ActivateDealResponse> => {
  const response = await axiosInstance.post("/activate_deal", {
    opportunity_id: workspaceId,
    company_id: dealId,
    next_stage: nextStage,
  });

  if (response.data.status === "error") {
    throw new Error(response.data.message || "Failed to activate deal");
  }

  return response.data;
};

export interface DeactivateDealResponse {
  status: string;
  message: string;
  data: {
    tracked: number;
  };
}

export const deactivateDeal = async (
  workspaceId: string,
  dealId: string
): Promise<DeactivateDealResponse> => {
  const response = await axiosInstance.post("/deactivate_deal", {
    opportunity_id: workspaceId,
    company_id: dealId,
  });

  if (response.data.status === "error") {
    throw new Error(response.data.message || "Failed to deactivate deal");
  }

  return response.data;
};

export interface SwapPremiumDealResponse {
  status: string;
  message: string;
}

export const swapPremiumDeal = async (
  workspaceId: string,
  dealId: string,
  demoteApplicationId: string
): Promise<SwapPremiumDealResponse> => {
  const response = await axiosInstance.post("/swap_premium_deal", {
    opportunity_id: workspaceId,
    company_id: dealId,
    demote_application_id: demoteApplicationId,
  });

  if (response.data.status === "error") {
    throw new Error(response.data.message || "Failed to swap premium deal");
  }

  return response.data;
};

// Founder invitation APIs
export const founderInvitationApi = {
  inviteFounderToCompany: async (
    companyId: string,
    founderEmail: string,
    founderName: string
  ) => {
    const response = await axiosInstance.post(
      `/company/${companyId}/invite-founder`,
      {
        founderEmail,
        founderName,
      }
    );
    return response.data;
  },

  getCompanyInvitationStatus: async (companyId: string) => {
    const response = await axiosInstance.get(
      `/company/${companyId}/invitation-status`
    );
    return response.data;
  },

  resendFounderInvitation: async (companyId: string) => {
    const response = await axiosInstance.post(
      `/company/${companyId}/resend-invitation`
    );
    return response.data;
  },

  cancelFounderInvitation: async (companyId: string) => {
    const response = await axiosInstance.post(
      `/company/${companyId}/cancel-invitation`
    );
    return response.data;
  },
};
